import React, { useEffect, useState } from "react";
import Title from "../../components/common/Title";
import { Topbar } from "../../components/include/Topbar";
import { Postdata } from "../../helpers/api.service";
import HttpService from "../../helpers/httpServices";
import { useDispatch, useSelector } from "react-redux";
import { message } from "../../redux/actions/toastActions";
import Page from "../../helpers/config";
import Pagination from "../../components/common/Pagination";
import { isPermission, isRole, isRoleType } from "../../redux/selectors/authSelector";
import Error403Page from "../ErrorPage";
import ModalPopup from "../../components/common/Modal";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import calender from "../../assets/Icons/calendar.png";
import filterIcon from "../../assets/Icons/filter.svg";
import ResetIcon from "../../assets/Icons/reset.svg";

const CreditHistory = () => {
  // Data & pagination state
  const [data, setData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(Page.records);
  const [currentPage, setCurrentPage] = useState(1);
  const [refresh, setRefresh] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  // Filter state
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [schoolId, setSchoolId] = useState("");
  const [performedBy, setPerformedBy] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterModal, setFilterModal] = useState(false);
  const [filterError, setFilterError] = useState("");

  // Dropdown data
  const [schoolList, setSchoolList] = useState([]);
  const [adminList, setAdminList] = useState([]);
  const [dropdownsLoading, setDropdownsLoading] = useState(true);

  const dispatch = useDispatch();
  const permissions = useSelector(isPermission);
  const role = useSelector(isRole);

  // Access check — super admin only
  const isAccessible = role === "admin" || role === "subadmin" || permissions?.includes("view_School");

  // Helper: format date to YYYY-MM-DD in local timezone
  const formatLocalDate = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Helper: format ISO date string to readable format
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }) + ", " + date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Fetch dropdown data on mount
  useEffect(() => {
    const fetchDropdowns = async () => {
      setDropdownsLoading(true);
      try {
        const [schoolRes, adminRes] = await Promise.all([
          Postdata(HttpService.credit_history_filter_schools, {}),
          Postdata(HttpService.credit_history_filter_admins, {}),
        ]);
        if (schoolRes.status === 200) {
          setSchoolList(schoolRes.data || []);
        }
        if (adminRes.status === 200) {
          setAdminList(adminRes.data || []);
        }
      } catch (err) {
        console.error("Error fetching dropdown data:", err);
      } finally {
        setDropdownsLoading(false);
      }
    };
    fetchDropdowns();
  }, []);

  // Fetch credit history list
  const fetchData = (page, limit) => {
    setLoader(true);
    const payload = {
      page: page,
      perpage: limit,
      search: searchQuery || "",
    };

    if (startDate) payload.startDate = formatLocalDate(startDate);
    if (endDate) payload.endDate = formatLocalDate(endDate);
    if (schoolId) payload.schoolId = schoolId;
    if (performedBy) payload.performedBy = performedBy;

    Postdata(HttpService.credit_history_list, payload)
      .then((res) => {
        if (res.status === 200) {
          setData(res.data.creditHistoryLogs || []);
          setTotalRows(res.data.count || 0);
        } else {
          dispatch(message(res.message || "Failed to fetch credit history"));
          setData([]);
          setTotalRows(0);
        }
        setHasFetched(true);
      })
      .catch((err) => {
        console.error("Error fetching credit history:", err);
        dispatch(message("Something went wrong while fetching credit history"));
        setData([]);
        setTotalRows(0);
        setHasFetched(true);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  // Fetch data on refresh, page, or perPage change
  useEffect(() => {
    fetchData(currentPage, perPage);
  }, [refresh]);

  // Debounced search
  useEffect(() => {
    if (searchQuery !== false) {
      const timer = setTimeout(() => setRefresh((prev) => !prev), 500);
      return () => clearTimeout(timer);
    }
  }, [searchQuery]);

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchData(page, perPage);
  };

  const handleRowsPerPageChange = (rows) => {
    setPerPage(rows);
    setCurrentPage(1);
    fetchData(1, rows);
  };

  // Filter validation & apply
  const handleApplyFilters = () => {
    setFilterError("");

    // Date validation
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (start > end) {
        setFilterError("Start Date should not be greater than End Date");
        return;
      }
    }

    setFilterModal(false);
    setCurrentPage(1);
    setRefresh((prev) => !prev);
  };

  // Reset filters
  const handleResetFilters = () => {
    setStartDate("");
    setEndDate("");
    setSchoolId("");
    setPerformedBy("");
    setFilterError("");
  };

  // Check if any filter is active
  const isFilterActive = startDate || endDate || schoolId || performedBy;

  if (!isAccessible) {
    return <Error403Page />;
  }

  return (
    <>
      <Title name="Credit History" />
      <Topbar title="Credit History" />

      {/* Filter Modal */}
      <ModalPopup
        className="alert-box-popup"
        show={filterModal}
        size="md"
        onHide={() => {
          setFilterModal(false);
          setFilterError("");
        }}
        heading="Filters"
      >
        <div className="text-start flex justify-center flex-col items-center gap-3 px-3 relative">
          <img
            src={ResetIcon}
            alt="Reset"
            className="absolute top-2 right-2 cursor-pointer"
            onClick={handleResetFilters}
            title="Reset Filters"
          />
          <h3>Filters</h3>

          {/* Filter Error */}
          {filterError && (
            <div className="w-full bg-red-50 border border-red-300 text-red-600 px-3 py-2 rounded-lg text-sm">
              {filterError}
            </div>
          )}

          {/* Start Date */}
          <div className="w-full">
            <label className="block mb-1 font-medium text-gray-700">
              Start Date
            </label>
            <div className="relative">
              <DatePicker
                selected={startDate}
                onChange={(date) => {
                  setStartDate(date);
                  setFilterError("");
                }}
                dateFormat="dd/MM/yyyy"
                className="w-96 p-3 bg-gray-100 rounded-xl pr-10"
                id="creditStartDate"
                name="startDate"
                placeholderText="dd/mm/yyyy"
                minDate={new Date("1900-01-01")}
                maxDate={new Date("9999-12-31")}
                onChangeRaw={(e) => e.preventDefault()}
                autoComplete="off"
              />
              <img
                src={calender}
                alt="Calendar Icon"
                className="absolute right-16 top-1/2 -translate-y-1/2 h-[0.8em] w-[0.8em] md:h-[1em] md:w-[1em] xl:h-[1.2em] xl:w-[1.2em] pointer-events-none"
              />
              {startDate && (
                <button
                  type="button"
                  className="absolute right-10 top-1/2 -translate-y-1/2 ml-2 text-gray-400 hover:text-gray-600 text-lg font-bold leading-none"
                  onClick={() => { setStartDate(""); setFilterError(""); }}
                  title="Clear Start Date"
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {/* End Date */}
          <div className="w-full">
            <label className="block mb-1 font-medium text-gray-700">
              End Date
            </label>
            <div className="relative">
              <DatePicker
                selected={endDate}
                onChange={(date) => {
                  setEndDate(date);
                  setFilterError("");
                }}
                dateFormat="dd/MM/yyyy"
                className="w-96 p-3 bg-gray-100 rounded-xl pr-10"
                id="creditEndDate"
                name="endDate"
                placeholderText="dd/mm/yyyy"
                minDate={startDate || new Date("1900-01-01")}
                maxDate={new Date("9999-12-31")}
                onChangeRaw={(e) => e.preventDefault()}
                popperClassName="enddate-popper"
                autoComplete="off"
              />
              <img
                src={calender}
                alt="Calendar Icon"
                className="absolute right-16 top-1/2 -translate-y-1/2 h-[0.8em] w-[0.8em] md:h-[1em] md:w-[1em] xl:h-[1.2em] xl:w-[1.2em] pointer-events-none"
              />
              {endDate && (
                <button
                  type="button"
                  className="absolute right-10 top-1/2 -translate-y-1/2 ml-2 text-gray-400 hover:text-gray-600 text-lg font-bold leading-none"
                  onClick={() => { setEndDate(""); setFilterError(""); }}
                  title="Clear End Date"
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {/* School Name Dropdown */}
          <div className="w-full">
            <label className="block mb-1 font-medium text-gray-700">
              School Name
            </label>
            <select
              className="w-full p-3 bg-gray-100 rounded-xl"
              value={schoolId}
              onChange={(e) => setSchoolId(e.target.value)}
              disabled={dropdownsLoading}
            >
              <option value="">All Schools</option>
              {schoolList.map((school) => (
                <option key={school._id} value={school._id}>
                  {school.name}
                </option>
              ))}
            </select>
          </div>

          {/* Added By Dropdown */}
          <div className="w-full">
            <label className="block mb-1 font-medium text-gray-700">
              Added By
            </label>
            <select
              className="w-full p-3 bg-gray-100 rounded-xl"
              value={performedBy}
              onChange={(e) => setPerformedBy(e.target.value)}
              disabled={dropdownsLoading}
            >
              <option value="">All Admins</option>
              {adminList.map((admin) => (
                <option key={admin._id} value={admin._id}>
                  {admin.name} ({admin.role})
                </option>
              ))}
            </select>
          </div>

          {/* Apply Button */}
          <div className="w-full flex justify-between gap-3 mb-2 mt-2">
            <button
              className="button-field-primary"
              style={{ width: "100%" }}
              onClick={handleApplyFilters}
            >
              Apply
            </button>
          </div>
        </div>
      </ModalPopup>

      {/* Page Content */}
      <div className="p-6 space-y-6 overflow-hidden">
        {/* Filter + Search Bar */}
        <div className="flex justify-start pl-2">
          <div className="flex gap-2 items-center">
           
            <input
              type="text"
              placeholder="Search by school, admin..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:outline-none w-72"
            />
             <div
              className={`bg-white text-black px-[15px] py-2 rounded-lg whitespace-nowrap flex items-center cursor-pointer relative ${
                isFilterActive ? "ring-2 ring-primary_main" : ""
              }`}
              onClick={() => setFilterModal(true)}
            >
              <img src={filterIcon} alt="Filter" />
              {isFilterActive && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-primary_main rounded-full"></span>
              )}
            </div>
          </div>
        </div>

        {/* Credit History Table */}
        <div className="bg-white rounded overflow-x-auto p-2">
          <table className="min-w-full text-sm text-left">
            <thead className="text-gray-700">
              <tr>
                <th className="px-4 py-3 font-medium whitespace-nowrap">Date</th>
                <th className="px-4 py-3 font-medium whitespace-nowrap">School Name</th>
                <th className="px-4 py-3 font-medium whitespace-nowrap">Previous Credit</th>
                <th className="px-4 py-3 font-medium whitespace-nowrap">Credits</th>
                <th className="px-4 py-3 font-medium whitespace-nowrap">Current Credit</th>
                <th className="px-4 py-3 font-medium whitespace-nowrap">Transaction Type</th>
                <th className="px-4 py-3 font-medium whitespace-nowrap">Performed By</th>
                <th className="px-4 py-3 font-medium whitespace-nowrap">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loader || !hasFetched ? (
                <tr>
                  <td colSpan="8" className="text-center py-10">
                    <div className="flex items-center justify-center gap-2 text-gray-500">
                      <svg
                        className="animate-spin h-5 w-5 text-primary_main"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : (
                data.map((item, index) => (
                  <tr key={item._id || index} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-gray-600">
                      {formatDisplayDate(item.createdAt)}
                    </td>
                    <td className="px-4 py-4 font-medium text-gray-800 min-w-[150px]">
                      {item.schoolName || "-"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-gray-700">
                      {item.previousCredit ?? "-"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span
                        className={`font-semibold ${
                          item.creditDifference > 0
                            ? "text-green-600"
                            : item.creditDifference < 0
                            ? "text-red-600"
                            : "text-gray-600"
                        }`}
                      >
                        {item.creditDifference > 0
                          ? `+${item.creditDifference}`
                          : item.creditDifference}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-gray-700">
                      {item.currentCredit ?? "-"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          item.transactionType === "Add"
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {item.transactionType || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-gray-700">
                      <div>
                        {item.performedByName || "-"}
                        {item.performedByRole && (
                          <span className="block text-xs text-gray-400 capitalize">
                            {item.performedByRole}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-gray-600 min-w-[200px]" title={item.description}>
                      {item.description || "-"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Empty State */}
          {!loader && hasFetched && data.length === 0 && (
            <div className="h-[50vh] flex items-center justify-center w-full">
              <div className="text-center">
                <h1 className="text-gray-400 text-lg">No Credit History Found</h1>
                <p className="text-gray-300 text-sm mt-1">
                  {isFilterActive
                    ? "Try adjusting your filters"
                    : "No credit transactions have been recorded yet"}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalRows={totalRows}
          perPage={perPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          type=""
        />
      </div>
    </>
  );
};

export default CreditHistory;
